import { StrictMode, useEffect } from 'react';
import { createRoot } from 'react-dom/client';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { LanguageProvider } from './i18n/LanguageContext';
import { useAuthStore } from './auth/authStore';
import { ProtectedRoute } from './components/ProtectedRoute/ProtectedRoute';
import LandingPage from './pages/LandingPage/LandingPage';
import UserPage    from './pages/UserPage/UserPage';
import App         from './App';
import AdminApp    from './AdminApp';
import { RankingPage } from './pages/ranking/RankingPage';
import './App.css';

// ─────────────────────────────────────────────────────────────────────────────
// Root — handles session hydration before rendering protected routes
// ─────────────────────────────────────────────────────────────────────────────

function Root() {
  const hydrate = useAuthStore(s => s.hydrate);

  // Restore persisted session once on startup
  useEffect(() => {
    hydrate();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <LanguageProvider>
      <BrowserRouter>
        <Routes>
          {/* ── Public ────────────────────────────────────────────────── */}
          <Route path="/" element={<LandingPage />} />

          {/*
           * Intentionally outside role="user" (like /play below) — admins use
           * this to enter the Hub as a player and check it live, via the
           * "Mapa Hub" button in the admin sidebar.
           */}
          <Route element={<ProtectedRoute />}>
            <Route path="/user" element={<UserPage />} />
          </Route>

          {/*
           * Game route — any authenticated user (users AND admins).
           * Intentionally outside StrictMode: StrictMode's double-mount
           * teardown calls .close() on a still-CONNECTING WebSocket, which
           * Chrome always logs as an uncatchable warning.
           * Intentionally outside role="user" so admins can spectate/play too.
           */}
          <Route element={<ProtectedRoute />}>
            <Route path="/play" element={<App />} />
            <Route path="/ranking" element={<RankingPage />} />
          </Route>

          {/* ── Protected — admins ────────────────────────────────────── */}
          <Route element={<ProtectedRoute role="admin" />}>
            {/*
             * Admin panel in StrictMode — uses only HTTP APIs (no sockets),
             * so double-mount is safe.
             */}
            <Route
              path="/admin/*"
              element={<StrictMode><AdminApp /></StrictMode>}
            />
          </Route>

          {/* ── Fallback ──────────────────────────────────────────────── */}
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </BrowserRouter>
    </LanguageProvider>
  );
}

// ─────────────────────────────────────────────────────────────────────────────

const domRoot = document.getElementById('root');
if (!domRoot) throw new Error('#root element not found in index.html');

createRoot(domRoot).render(<Root />);
